import React, { useEffect, useState } from "react";
import { Button, Input, Modal, Progress, Space } from "antd";
import { PlusOutlined } from "@ant-design/icons";
import { message } from "../message";
import { fetchEventData } from "fetch-sse";
import { trace, useClickTrace, getClientId } from "../behavior";

declare const process: {
  env: {
    SERVER_PATH: string;
  };
};

const txts = [
  "小学一年级词汇",
  "英文字母",
  "古诗词",
  "常用汉字",
  "常用英文单词",
];

let ctrl: AbortController | null = null;

const TYPING_SYSTEM_PROMPT = `负责设计打字练习文本，只返回练习内容信息。

参考如下：
例如1：小学一年级词汇，返回：爸爸 妈妈 我们 大米 土地 马儿 鲜花 大哥 小弟 个子 画画

例如2：选取一篇有趣的一天文章，返回：今天早上，太阳公公笑眯眯地爬上天空，我背着小书包，一蹦一跳地去上小学。走进教室，老师已经在等我们啦，她的声音甜甜的，像草莓味的牛奶。

例如3：常用英文单词，返回:
hello world thank you please sorry time day friend happy###你好 世界 谢谢 你 请 对不起 时间 天 朋友 快乐
备注：###后是单词中文翻译，每个英文词对应一个翻译中文词

例如4：古诗词2首，返回: 山居：青山绿水绕村居，竹影松声入画图。野老相逢无别事，一壶浊酒话桑榆。 秋夜：秋风萧瑟夜微凉，月照孤窗影自长。遥想故园千里外，何时归去看爹娘。

例如5：生成英文字母，返回：ABCDEFGHIJKLMNOPQRSTUVWXYZ

例如6：小学三年级上册语文第六单元，返回：采莲曲：荷叶罗裙一色裁，芙蓉向脸两边开。乱入池中看不见，闻歌始觉有人来。

注意：只返回练习内容（英文返回###翻译）`;

/**
 * 创建一个永远不会达到100%的非线性进度条函数
 * @param options 配置选项
 * @returns 返回当前进度的函数
 */
function createInfiniteProgressTracker(
  options: {
    initialProgress?: number; // 初始进度（可选，默认0）
    incrementBase?: number; // 基础增量（可选，默认1.0）
    slowdownFactor?: number; // 减速因子（可选，默认3.0）
    cap?: number; // 进度上限（可选，默认99.99%）
  } = {}
): () => number {
  const {
    initialProgress = 0,
    incrementBase = 1.0,
    slowdownFactor = 3.0,
    cap = 99.99,
  } = options;

  let currentProgress = Math.min(initialProgress, cap - 0.01); // 确保初始值低于上限

  /**
   * 获取并更新当前进度
   * @returns 当前进度值（0 - cap）
   */
  return function getProgress(): number {
    // 计算非线性增量：越接近上限，增量越小
    const normalizedProgress = currentProgress / cap;
    const slowdownEffect = 1 + normalizedProgress * slowdownFactor;
    const increment =
      (incrementBase / slowdownEffect) * (1 - normalizedProgress);

    // 增加进度（确保不超过上限）
    currentProgress = Math.min(currentProgress + increment, cap);

    return currentProgress;
  };
}

// 使用示例
const getProgress = createInfiniteProgressTracker({
  incrementBase: 2.0, // 初始增长较快
  slowdownFactor: 5.0, // 减速效果明显
  cap: 99.9, // 进度上限设为99.9%
});

const CreateNewButton = (props: {
  onCreate?: (text: string, availableKeyCodes: string[]) => void;
  onInit?: (showModal: () => void) => void;
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [inputValue, setInputValue] = useState("");
  const [loading, setLoading] = useState(false);
  const [percent, setPercent] = useState(100);

  const showModal = useClickTrace("新建练习",() => {
    setIsModalOpen(true);
  });

  const handleOk = async () => {
    if (!inputValue) {
      message.error("请输入要练习的内容");
      return;
    }
    setLoading(true);
    try {
      let err;
      let ret = "";
      ctrl = new AbortController();
      trace("click", { name: '生成', value: inputValue });
      await fetchEventData(
        `${process.env.SERVER_PATH}/api/llm/generate`,
        {
          method: "POST",
          signal: ctrl?.signal,
          headers: {
            //   Authorization: token ? `Bearer ${token}` : "",
            "Content-Type": "application/json",
            "Cache-Control": "no-cache",
            Pragma: "no-cache",
            // 添加一个唯一的自定义头部，包含当前时间戳
            "X-Custom-No-Cache": new Date().getTime().toString(),
            "X-Client-Id": getClientId(),
          },
          data: {
            question: "生成" + inputValue,
            systemPrompt: TYPING_SYSTEM_PROMPT,
          },
          onError(error) {
            err = error;
          },
          async onMessage(ev) {
            const data = JSON.parse(ev!.data);
            console.log(ev?.event, data);
            if (ev?.event === "MESSAGE") {
              setIsModalOpen(false);
              setPercent(getProgress());
              ret += data.content;
              props.onCreate?.(ret.replaceAll("\\n", ""), []);
            }
            if (ev?.event === "ERROR") {
              err = data.message;
            }
          },
        }
      );
      if (err) {
        throw new Error(err);
      }
      setPercent(100);
      message.success("练习内容创建成功");
      setLoading(false);
    } catch (e: any) {
      setLoading(false);
      if (e.message.includes("AbortError")) {
        setPercent(100);
        message.info("练习内容创建已取消");
        return;
      }
      message.error(e.message);
      trace("error", { name: '生成失败', value: e.message });
    }
  };

  const handleCancel = () => {
    ctrl?.abort();
    setIsModalOpen(false);
  };

  useEffect(() => {
    props.onInit?.(showModal.onClick);
  }, [props, props.onInit, showModal.onClick]);

  return (
    <>
      {percent !== 100 && (
        <Progress
          percent={percent}
          showInfo={false}
          size="small"
          strokeColor="#1677ff"
        />
      )}
      {percent !== 100 ? (
        <Button
          type="text"
          className="cancel-button"
          size="small"
          danger
          onClick={handleCancel}
        >
          取消
        </Button>
      ) : (
        <Button
          type="text"
          icon={<PlusOutlined />}
          title="新建练习"
          {...showModal}
        ></Button>
      )}
      <Modal
        title="新建练习"
        closable={{ "aria-label": "Custom Close Button" }}
        open={isModalOpen}
        onOk={handleOk}
        onCancel={handleCancel}
        okButtonProps={{ loading }}
        okText="创建"
        cancelText="取消"
      >
        <div style={{ margin: "5px 0" }}>
          输入要练习的内容：
          <Space.Compact size="small" className="create-new-button-space">
            {txts.map((txt, i) => (
              <Button key={i} onClick={() => setInputValue(txt)}>
                {txt}
              </Button>
            ))}
          </Space.Compact>
        </div>
        <Input.TextArea
          value={inputValue}
          allowClear
          onChange={(e) => setInputValue(e.target.value)}
          autoSize={{ minRows: 3, maxRows: 6 }}
          placeholder="请输入要练习的内容，AI为您智能生成练习内容，例如：小学一年级词汇"
        ></Input.TextArea>
      </Modal>
    </>
  );
};

export default CreateNewButton;
